import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import {
  LayoutDashboard,
  Users,
  ClipboardList,
  Calendar,
  FolderOpen,
  DollarSign,
  Building,
  TrendingUp,
  BookOpen,
  UserCheck,
  Settings,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

const Sidebar = ({ collapsed, onToggleCollapse }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem("theme") === "dark"
  );
  const [academicOpen, setAcademicOpen] = useState(false);
  const [operationsOpen, setOperationsOpen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const isStudent = user?.role === "student";
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.add("transition-colors", "duration-500");

    if (darkMode) {
      root.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      root.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [darkMode]);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const isActive = (path) => location.pathname === path;

  // Close accordions when sidebar is collapsed
  useEffect(() => {
    if (collapsed) {
      setAcademicOpen(false);
      setOperationsOpen(false);
      setAdminOpen(false);
    }
  }, [collapsed]);

  return (
    <>
      {!isStudent && (
        <aside
          className={`fixed md:static top-0 md:top-auto left-0 h-full md:h-auto z-40 bg-indigo-900 text-white shadow-lg transition-all duration-300 ease-in-out transform ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
          } ${collapsed ? "w-16" : "w-64"}`}
        >
          {/* Collapse Toggle Button */}
          <div className="absolute top-2 right-1 z-20">
            <button
              onClick={onToggleCollapse}
              className="p-1 rounded-full bg-indigo-800 hover:bg-indigo-700 transition-colors duration-200 shadow-lg"
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <ChevronLeft 
                size={14} 
                className={`transform transition-transform duration-200 ${
                  collapsed ? 'rotate-180' : ''
                }`}
              />
            </button>
          </div>

          <nav className={`pt-10 p-4 flex flex-col space-y-2 ${collapsed ? "px-2" : ""}`}>
            {/* Core Management - Always visible */}
            <Link
              to="/dashboard"
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center p-2 rounded hover:bg-indigo-600 hover:text-white transition-all duration-200 no-underline ${
                isActive("/dashboard")
                  ? "bg-indigo-600 text-white font-semibold"
                  : ""
              } ${collapsed ? "justify-center w-12 h-12 mx-auto" : "gap-2"}`}
              title={collapsed ? "Dashboard" : ""}
            >
              <LayoutDashboard size={20} />
              {!collapsed && <span>Dashboard</span>}
            </Link>
            
            <Link
              to="/students"
              className={`flex items-center p-2 rounded transition-all duration-200 hover:bg-indigo-600 hover:text-white no-underline ${
                isActive("/students")
                  ? "bg-indigo-600 text-white font-semibold"
                  : ""
              } ${collapsed ? "justify-center w-12 h-12 mx-auto" : "gap-2"}`}
              title={collapsed ? "Students" : ""}
            >
              <Users size={20} />
              {!collapsed && <span>Students</span>}
            </Link>

            <Link
              to="/tasks"
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center p-2 rounded transition-all duration-200 hover:bg-indigo-600 hover:text-white no-underline ${
                isActive("/tasks")
                  ? "bg-indigo-600 text-white font-semibold"
                  : ""
              } ${collapsed ? "justify-center w-12 h-12 mx-auto" : "gap-2"}`}
              title={collapsed ? "Tasks" : ""}
            >
              <ClipboardList size={20} />
              {!collapsed && <span>Tasks</span>}
            </Link>

            <Link
              to="/projects"
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center p-2 rounded transition-all duration-200 hover:bg-indigo-600 hover:text-white no-underline ${
                isActive("/projects")
                  ? "bg-indigo-600 text-white font-semibold"
                  : ""
              } ${collapsed ? "justify-center w-12 h-12 mx-auto" : "gap-2"}`}
              title={collapsed ? "Projects" : ""}
            >
              <FolderOpen size={20} />
              {!collapsed && <span>Projects</span>}
            </Link>

            {/* Academic Management Accordion */}
            <div className="border-t border-indigo-700 pt-2 mt-2">
              <button
                onClick={() => !collapsed && setAcademicOpen(!academicOpen)}
                className={`flex items-center p-2 rounded hover:bg-indigo-600 hover:text-white transition-all duration-200 text-left ${
                  collapsed 
                    ? "justify-center w-12 h-12 mx-auto" 
                    : "w-full justify-between gap-2"
                }`}
                title={collapsed ? "Academic" : ""}
              >
                <div className={`flex items-center ${collapsed ? "" : "gap-2"}`}>
                  <BookOpen size={20} />
                  {!collapsed && <span>Academic</span>}
                </div>
                {!collapsed && (academicOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
              </button>
              
              {academicOpen && !collapsed && (
                <div className="ml-4 mt-1 space-y-1">
                  <Link
                    to="/skills"
                    className={`flex items-center gap-2 p-2 rounded hover:bg-indigo-600 hover:text-white no-underline text-sm ${
                      isActive("/skills")
                        ? "bg-indigo-600 text-white font-semibold"
                        : ""
                    }`}
                  >
                    <ClipboardList size={16} />
                    <span>Skills</span>
                  </Link>

                  <Link
                    to="/courses"
                    className={`flex items-center gap-2 p-2 rounded hover:bg-indigo-600 hover:text-white no-underline text-sm ${
                      isActive("/courses")
                        ? "bg-indigo-600 text-white font-semibold"
                        : ""
                    }`}
                  >
                    <ClipboardList size={16} />
                    <span>Courses</span>
                  </Link>

                  <Link
                    to="/lessons"
                    className={`flex items-center gap-2 p-2 rounded hover:bg-indigo-600 hover:text-white no-underline text-sm ${
                      isActive("/lessons")
                        ? "bg-indigo-600 text-white font-semibold"
                        : ""
                    }`}
                  >
                    <ClipboardList size={16} />
                    <span>Lessons</span>
                  </Link>

                  <Link
                    to="/classes"
                    className={`flex items-center gap-2 p-2 rounded hover:bg-indigo-600 hover:text-white no-underline text-sm ${
                      isActive("/classes")
                        ? "bg-indigo-600 text-white font-semibold"
                        : ""
                    }`}
                  >
                    <ClipboardList size={16} />
                    <span>Classes</span>
                  </Link>

                  <Link
                    to="/materials"
                    className={`flex items-center gap-2 p-2 rounded hover:bg-indigo-600 hover:text-white no-underline text-sm ${
                      isActive("/materials")
                        ? "bg-indigo-600 text-white font-semibold"
                        : ""
                    }`}
                  >
                    <BookOpen size={16} />
                    <span>Materials</span>
                  </Link>

                  <Link
                    to="/batches"
                    className={`flex items-center gap-2 p-2 rounded hover:bg-indigo-600 hover:text-white no-underline text-sm ${
                      isActive("/batches")
                        ? "bg-indigo-600 text-white font-semibold"
                        : ""
                    }`}
                  >
                    <ClipboardList size={16} />
                    <span>Batches</span>
                  </Link>
                </div>
              )}
            </div>

            {/* Operations Accordion */}
            <div>
              <button
                onClick={() => !collapsed && setOperationsOpen(!operationsOpen)}
                className={`flex items-center p-2 rounded hover:bg-indigo-600 hover:text-white transition-all duration-200 text-left ${
                  collapsed 
                    ? "justify-center w-12 h-12 mx-auto" 
                    : "w-full justify-between gap-2"
                }`}
                title={collapsed ? "Operations" : ""}
              >
                <div className={`flex items-center ${collapsed ? "" : "gap-2"}`}>
                  <UserCheck size={20} />
                  {!collapsed && <span>Operations</span>}
                </div>
                {!collapsed && (operationsOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
              </button>
              
              {operationsOpen && !collapsed && (
                <div className="ml-4 mt-1 space-y-1">
                  <Link
                    to="/attendance"
                    className={`flex items-center gap-2 p-2 rounded hover:bg-indigo-600 hover:text-white no-underline text-sm ${
                      isActive("/attendance")
                        ? "bg-indigo-600 text-white font-semibold"
                        : ""
                    }`}
                  >
                    <ClipboardList size={16} />
                    <span>Attendance</span>
                  </Link>

                  <Link
                    to="/leaves"
                    className={`flex items-center gap-2 p-2 rounded hover:bg-indigo-600 hover:text-white no-underline text-sm ${
                      isActive("/leaves")
                        ? "bg-indigo-600 text-white font-semibold"
                        : ""
                    }`}
                  >
                    <Calendar size={16} />
                    <span>Leaves</span>
                  </Link>

                  {/* Show Colleges for admin and tutor users */}
                  {(isAdmin || user?.role === "tutor") && (
                    <Link
                      to="/colleges"
                      className={`flex items-center gap-2 p-2 rounded hover:bg-indigo-600 hover:text-white no-underline text-sm ${
                        isActive("/colleges")
                          ? "bg-indigo-600 text-white font-semibold"
                          : ""
                      }`}
                    >
                      <Building size={16} />
                      <span>Colleges</span>
                    </Link>
                  )}
                </div>
              )}
            </div>

            {/* Administrative Section - Admin Only */}
            {isAdmin && (
              <div className="border-t border-indigo-700 pt-2">
                  <button
                    onClick={() => !collapsed && setAdminOpen(!adminOpen)}
                    className={`flex items-center p-2 rounded hover:bg-indigo-600 hover:text-white transition-all duration-200 text-left ${
                      collapsed 
                        ? "justify-center w-12 h-12 mx-auto" 
                        : "w-full justify-between gap-2"
                    }`}
                    title={collapsed ? "Administration" : ""}
                  >
                    <div className={`flex items-center ${collapsed ? "" : "gap-2"}`}>
                      <Settings size={20} />
                      {!collapsed && <span>Administration</span>}
                    </div>
                    {!collapsed && (adminOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
                  </button>                {adminOpen && !collapsed && (
                  <div className="ml-4 mt-1 space-y-1">
                    <Link
                      to="/tutors"
                      className={`flex items-center gap-2 p-2 rounded hover:bg-indigo-600 hover:text-white no-underline text-sm ${
                        isActive("/tutors")
                          ? "bg-indigo-600 text-white font-semibold"
                          : ""
                      }`}
                    >
                      <Users size={16} />
                      <span>Tutors</span>
                    </Link>

                    <Link
                      to="/revenue"
                      className={`flex items-center gap-2 p-2 rounded hover:bg-indigo-600 hover:text-white no-underline text-sm ${
                        isActive("/revenue")
                          ? "bg-indigo-600 text-white font-semibold"
                          : ""
                      }`}
                    >
                      <TrendingUp size={16} />
                      <span>Revenue</span>
                    </Link>

                    <Link
                      to="/salary"
                      className={`flex items-center gap-2 p-2 rounded hover:bg-indigo-600 hover:text-white no-underline text-sm ${
                        isActive("/salary")
                          ? "bg-indigo-600 text-white font-semibold"
                          : ""
                      }`}
                    >
                      <DollarSign size={16} />
                      <span>Salary</span>
                    </Link>

                    <Link
                      to="/fees"
                      className={`flex items-center gap-2 p-2 rounded hover:bg-indigo-600 hover:text-white no-underline text-sm ${
                        isActive("/fees")
                          ? "bg-indigo-600 text-white font-semibold"
                          : ""
                      }`}
                    >
                      <DollarSign size={16} />
                      <span>Fee Management</span>
                    </Link>
                  </div>
                )}
              </div>
            )}
          </nav>
        </aside>
      )}
    </>
  );
};
export default Sidebar;
